import { createContext, useContext, useState, useRef, useEffect } from "react";
import useGetData from "../useGetData";
import { useUndo } from "./useUndo";

const MainContext = createContext();

export const useMainContext = () => {
  const context = useContext(MainContext);
  if (!context) {
    throw new Error("useMainContext must be used within a MainContextProvider");
  }
  return context;
};

export const MainContextProvider = ({ children, user }) => {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]); // Array of full influencer objects
  const [selectAll, setSelectAll] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "username", direction: "asc" });
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [showOnlyApproved, setShowOnlyApproved] = useState(false);
  const [checkStatusFilter, setCheckStatusFilter] = useState("");
  const [filters, setFilters] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [page, setPage] = useState(0);
  const [rowsCount, setRowsCount] = useState(0);
  const [currentNoteInfluencer, setCurrentNoteInfluencer] = useState(null);
  const [filtersLogic, setFiltersLogic] = useState("and");
  const [currentColumnPreset, setCurrentColumnPreset] = useState("default");
  const [availableUsers, setAvailableUsers] = useState([]);
  const isGuest = false; // user?.role === "guest"; // fixed to be equal with Staff role
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  const [currentAudienceData, setCurrentAudienceData] = useState(null);
  const [textFieldEditModalOpen, setTextFieldEditModalOpen] = useState(false);
  const [currentTextFieldData, setCurrentTextFieldData] = useState(null);
  const [allDataForExport, setAllDataForExport] = useState([]);
  const selectAllCheckboxRef = useRef(null);

  // Undo functionality
  const {
    undoState,
    setUndoState,
    saveUndoData,
    handleUndoRedo,
  } = useUndo();

  useEffect(() => {
    if (!checkStatusFilter) return;
    setFilters([]);
  }, [checkStatusFilter]);

  useEffect(() => {
    if (!filters?.length) return;
    setCheckStatusFilter("");
  }, [filters]);

  const { fetchDataTable, fetchDataForExport } = useGetData({
    setAvailableUsers,
    setRows,
    setColumns,
    setLoadingData,
    setIsInitialLoad,
    setCurrentColumnPreset,
    setVisibleColumns,
    columns,
    setFilters,
    setFiltersLogic,
    rowsPerPage,
    page,
    setRowsCount,
    rows,
    searchQuery,
    setSearchQuery,
    sortConfig,
    setSortConfig,
    showOnlyApproved,
    checkStatusFilter,
    filters,
    filtersLogic,
    setCheckStatusFilter,
    setPage,
    setRowsPerPage,
    setAllDataForExport,
  });

  const value = {
    // State
    columns,
    rows,
    selected,
    selectAll,
    loadingData,
    isInitialLoad,
    searchQuery,
    sortConfig,
    visibleColumns,
    showOnlyApproved,
    checkStatusFilter,
    filters,
    rowsPerPage,
    page,
    currentNoteInfluencer,
    filtersLogic,
    currentColumnPreset,
    availableUsers,
    audienceModalOpen,
    currentAudienceData,
    textFieldEditModalOpen,
    currentTextFieldData,
    allDataForExport,
    selectAllCheckboxRef,
    isGuest,
    user,
    rowsCount,

    // Undo state
    undoState,
    setUndoState,

    // Setters
    setColumns,
    setRows,
    setSelected,
    setSelectAll,
    setLoadingData,
    setIsInitialLoad,
    setSearchQuery,
    setSortConfig,
    setVisibleColumns,
    setShowOnlyApproved,
    setCheckStatusFilter,
    setFilters,
    setRowsPerPage,
    setPage,
    setCurrentNoteInfluencer,
    setFiltersLogic,
    setCurrentColumnPreset,
    setAvailableUsers,
    setAudienceModalOpen,
    setCurrentAudienceData,
    setTextFieldEditModalOpen,
    setCurrentTextFieldData,
    setAllDataForExport,
    setRowsCount,
    fetchDataTable,
    fetchDataForExport,

    // Undo functions
    saveUndoData,
    handleUndoRedo,
  };

  return <MainContext.Provider value={value}>{children}</MainContext.Provider>;
};
